import React from 'react';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';
import COLORS from '../../constants/colors';
import { SideTabDescriptor, SideTabSection } from './types';

export const sideTabId = (value: string) => `side-tab-${value}`;
export const sideTabPanelId = (value: string) => `side-tabpanel-${value}`;

const TabButton = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  minHeight: '38px',
  padding: '8px 12px',
  borderRadius: '8px',
  marginBottom: '2px',
  color: COLORS.DARK_GREY,
  gap: '10px',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: alpha(COLORS.MAIN_DARK, 0.55),
    transition: theme.transitions.create('color', { duration: theme.transitions.duration.shortest }),
  },
  '&:hover': {
    backgroundColor: alpha(COLORS.MAIN_DARK, 0.04),
    color: COLORS.MAIN_DARK,
    '& .MuiSvgIcon-root': { color: COLORS.MAIN_DARK },
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
    outlineOffset: '-2px',
  },
  '&.Mui-disabled': {
    opacity: 0.45,
  },
  '&[aria-selected="true"]': {
    backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.12),
    color: '#1E72A8',
    '& .MuiSvgIcon-root': { color: '#1E72A8' },
    '&:hover': {
      backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.2),
      color: '#1E72A8',
      '& .MuiSvgIcon-root': { color: '#1E72A8' },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '18px',
      borderRadius: '0 3px 3px 0',
      backgroundColor: COLORS.PRIMARY_BLUE,
    },
  },
}));

interface SideTabsProps {
  sections: SideTabSection[];
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  isTabDisabled: (tab: SideTabDescriptor) => boolean;
}

export function SideTabs({ sections, ariaLabel, value, onChange, isTabDisabled }: SideTabsProps) {
  const buttonRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const enabledTabs = React.useMemo(
    () => sections.flatMap((section) => section.tabs).filter((tab) => !isTabDisabled(tab)),
    [sections, isTabDisabled],
  );

  /** Roving focus, as expected of a tablist: Up/Down step through enabled tabs, Home/End jump. */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key) || enabledTabs.length === 0) {
      return;
    }
    event.preventDefault();

    const currentIndex = enabledTabs.findIndex((tab) => tab.value === value);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % enabledTabs.length;
    } else if (event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else {
      nextIndex = enabledTabs.length - 1;
    }

    const nextTab = enabledTabs[nextIndex];
    if (nextTab) {
      onChange(nextTab.value);
      buttonRefs.current[nextTab.value]?.focus();
    }
  };

  return (
    <Box role="tablist" aria-orientation="vertical" aria-label={ariaLabel} onKeyDown={handleKeyDown}>
      {sections.map((section, sectionIndex) => (
        <Box key={section.title ?? `side-tab-section-${sectionIndex}`} sx={{ mt: sectionIndex === 0 ? 0 : '14px' }}>
          {section.title && (
            <Typography
              component="div"
              sx={{
                px: '12px',
                mb: '6px',
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: COLORS.LIGHT_GRAY,
              }}
            >
              {section.title}
            </Typography>
          )}
          {section.tabs.map((tab) => {
            const selected = tab.value === value;
            const disabled = isTabDisabled(tab);

            return (
              <TabButton
                key={tab.value}
                ref={(node: HTMLDivElement | null) => {
                  buttonRefs.current[tab.value] = node;
                }}
                id={sideTabId(tab.value)}
                role="tab"
                aria-selected={selected}
                aria-controls={sideTabPanelId(tab.value)}
                tabIndex={selected ? 0 : -1}
                disabled={disabled}
                onClick={() => onChange(tab.value)}
              >
                <tab.IconComponent />
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: selected ? 600 : 500,
                    lineHeight: '18px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {tab.label}
                </Typography>
              </TabButton>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
